'use client';

import { useTheme } from 'next-themes';
import { useEffect, useState } from 'react';

import { Skeleton } from '@/components/ui/skeleton';
import { appHasMounted, markAppMounted } from '@/hooks/appMounted';
import { theme } from '@/lib/ui/theme';

interface AudienceSectionProps {
  isLoading?: boolean;
}

const forAudience = [
  "Developers who'd rather catch it in review than in prod",
  'Solo founders without a staff engineer safety net',
  'Teams scaling AI-assisted development responsibly',
  "Engineers who don't trust green checkmarks",
  'Tech leads tired of policing agent behavior',
];

const notForAudience = [
  'Teams that want a rubber stamp, not a review',
  'Teams comfortable with silent regressions',
  'Teams that trust AI output by default',
  'Anyone who treats a passing build as proof',
  'Anyone who calls tests "optional"',
];

export function AudienceSection({ isLoading = false }: AudienceSectionProps) {
  const { resolvedTheme } = useTheme();
  const [mounted, setMounted] = useState(appHasMounted());

  useEffect(() => {
    markAppMounted();
    setMounted(true);
  }, []);

  const isDark = mounted && resolvedTheme === 'dark';

  if (isLoading || !mounted) {
    return (
      <section className="px-6 py-12 md:px-16 md:py-16">
        <div className="mx-auto max-w-[1200px]">
          <Skeleton className="mx-auto mb-8 h-10 w-[55%]" />
          <div className="grid gap-8 md:grid-cols-2">
            <Skeleton className="h-48 rounded-xl" />
            <Skeleton className="h-48 rounded-xl" />
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="px-6 py-12 md:px-16 md:py-16">
      <div className="mx-auto max-w-[1200px]">
        <h2
          className="text-gradient-hero mb-10 text-center text-3xl font-bold tracking-tight md:text-4xl"
          style={{ fontFamily: theme.fonts.display }}
        >
          Built for engineers who don&apos;t trust &quot;it passed CI&quot;.
        </h2>

        <div className="mx-auto grid max-w-[900px] gap-8 md:grid-cols-2">
          {/* This is for - primary accent (green) */}
          <div
            className="rounded-xl p-6"
            style={{
              background: isDark
                ? `${theme.colors.accent.primary.dark}1a`
                : `${theme.colors.accent.primary.light}14`,
              border: `1px solid ${isDark ? `${theme.colors.accent.primary.dark}4d` : `${theme.colors.accent.primary.light}33`}`,
            }}
          >
            <h3
              className="mb-4 text-lg font-semibold md:text-xl"
              style={{
                fontFamily: theme.fonts.display,
                color: theme.colors.accent.primary[isDark ? 'dark' : 'light'],
              }}
            >
              This is for:
            </h3>
            <ul
              className="space-y-2 text-base"
              style={{ fontFamily: theme.fonts.body }}
            >
              {forAudience.map((item) => (
                <li key={item} className="flex items-start gap-2">
                  <span
                    className="mt-1.5"
                    style={{
                      color:
                        theme.colors.accent.primary[isDark ? 'dark' : 'light'],
                    }}
                  >
                    ✓
                  </span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* This is not for - secondary accent (purple) */}
          <div
            className="rounded-xl p-6"
            style={{
              background: isDark
                ? `${theme.colors.accent.secondary.dark}1a`
                : `${theme.colors.accent.secondary.light}14`,
              border: `1px solid ${isDark ? `${theme.colors.accent.secondary.dark}4d` : `${theme.colors.accent.secondary.light}33`}`,
            }}
          >
            <h3
              className="mb-4 text-lg font-semibold md:text-xl"
              style={{
                fontFamily: theme.fonts.display,
                color: theme.colors.accent.secondary[isDark ? 'dark' : 'light'],
              }}
            >
              This is not for:
            </h3>
            <ul
              className="space-y-2 text-base"
              style={{ fontFamily: theme.fonts.body }}
            >
              {notForAudience.map((item) => (
                <li key={item} className="flex items-start gap-2">
                  <span
                    className="mt-1.5"
                    style={{
                      color:
                        theme.colors.accent.secondary[
                          isDark ? 'dark' : 'light'
                        ],
                    }}
                  >
                    ✗
                  </span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}
